import ENDPOINT from 'constants/endPoint';
import { apiErrorHandler, convertIsoDateToString, showToastError } from 'utils';
import { formatNumber } from 'utils/currencyFormat';

// Mirrors BE FLIGHT_BOOKING_STATUS (api-contract.md, Enums).
// Unknown values render as the raw string.
export const FLIGHT_BOOKING_STATUS_LABELS = {
  WAITING_PAYMENT: 'Chờ thanh toán',
  ISSUING: 'Đang xuất vé',
  ISSUED: 'Đã xuất vé',
  ISSUE_FAILED: 'Xuất vé thất bại',
  COMPLETED: 'Đã hoàn thành',
  CANCELLED: 'Đã huỷ',
  EXPIRED: 'Hết hạn giữ chỗ',
  WAITING_FOR_REFUND: 'Chờ hoàn tiền',
  REFUNDED: 'Đã hoàn tiền',
};

export const getFlightStatusLabel = (status) => (
  FLIGHT_BOOKING_STATUS_LABELS[status] || status || '-'
);

// Mirrors BE FlightBooking.tripType (phase-2-remaining.md §2): a two-leg trip that does not
// return to its origin is MULTI_CITY too, so this is display-only — the admin never derives it.
export const TRIP_TYPE_LABELS = {
  ONE_WAY: 'Một chiều',
  ROUND_TRIP: 'Khứ hồi',
  MULTI_CITY: 'Nhiều chặng',
};

// "Chiều đi" / "Chiều về" on a one-way/round trip; "Chặng n" (n = leg + 1) on multi-city
// (phase-2-remaining.md §2). tripType is optional so every existing one/two-leg caller keeps
// working unchanged.
export const formatLeg = (leg, tripType) => {
  if (tripType === 'MULTI_CITY') return `Chặng ${leg + 1}`;
  return leg === 1 ? 'Chiều về' : 'Chiều đi';
};

export const FLIGHT_BOOKING_STATUS_OPTIONS = Object.entries(FLIGHT_BOOKING_STATUS_LABELS)
  .map(([value, label]) => ({ value, label }));

export const formatFlightAmount = (amount, currency) => (
  amount === undefined || amount === null ? '-' : `${formatNumber(amount)} ${currency || ''}`.trim()
);

export const formatFlightDateTime = (date) => (
  convertIsoDateToString(date, 'DD/MM/YYYY HH:mm') || '-'
);

// Mirrors BE seat status (seat-selection.md §4.3). REQUESTED only survives a failed read-back.
export const SEAT_STATUS_LABELS = {
  REQUESTED: 'Chờ xác nhận',
  CONFIRMED: 'Đã xác nhận',
  UNAVAILABLE: 'Không giữ được',
};

// One line per seat. The leg prefix only helps when there's more than one leg.
export const formatFlightSeat = (seat, currency, showLeg, tripType) => {
  const legLabel = formatLeg(seat.leg, tripType);
  return [
    showLeg ? `${legLabel} ${seat.number}` : seat.number,
    formatFlightAmount(seat.price, currency),
    SEAT_STATUS_LABELS[seat.status] || seat.status,
  ].join(' · ');
};

export const hasDroppedSeat = (passengers) => passengers.some((passenger) => (
  (passenger.seats || []).some((seat) => seat.status === 'UNAVAILABLE')
));

// Mirrors BE passengers[].tickets[].serviceType (BE f436e64). VJ and VU answer a seat, baggage
// or service fee with a plain TKT line, so without the tag a receipt reads as an e-ticket.
// FLIGHT is the fare itself and needs no tag.
export const TICKET_SERVICE_TYPE_LABELS = {
  SEAT: 'Ghế',
  EXCHANGE: 'Đổi vé',
  BAGGAGE: 'Hành lý',
  SSR: 'Dịch vụ',
};

// One line per ticket. The service tag is appended only for a fee line.
export const formatFlightTicket = (ticket) => [
  ticket.ticketNumber,
  ticket.ticketType || '-',
  ticket.ticketStatus || '-',
  ticket.serviceType && ticket.serviceType !== 'FLIGHT'
    && (TICKET_SERVICE_TYPE_LABELS[ticket.serviceType] || ticket.serviceType),
].filter(Boolean).join(' · ');

// Mirrors BE passengers[].extras[].kind / status (ancillaries.md §3.2-3.3).
export const EXTRA_KIND_LABELS = {
  BAGGAGE: 'Hành lý ký gửi',
  MEAL: 'Suất ăn',
  OTHER: 'Dịch vụ khác',
};

export const EXTRA_STATUS_LABELS = {
  REQUESTED: 'Chờ xác nhận',
  CONFIRMED: 'Đã xác nhận',
  DROPPED: 'Không giữ được',
};

// One line per paid service (baggage, meal, other). The leg prefix only helps on a round trip;
// the name is the API's own text, no paraphrase. status is omitted when absent: a checkout
// extra always carries one, a post-ticketing purchase item never does (the purchase entry
// itself has one status, shown once, not per item — post-ticketing-extras.md §3.3).
export const formatFlightExtra = (extra, currency, showLeg, tripType) => {
  const legLabel = formatLeg(extra.leg, tripType);
  const kind = EXTRA_KIND_LABELS[extra.kind] || extra.kind;
  return [
    `${showLeg ? `${legLabel} ` : ''}${kind}: ${extra.name}`,
    formatFlightAmount(extra.price, currency),
    extra.status && (EXTRA_STATUS_LABELS[extra.status] || extra.status),
  ].filter(Boolean).join(' · ');
};

export const hasDroppedExtra = (passengers) => passengers.some((passenger) => (
  (passenger.extras || []).some((extra) => extra.status === 'DROPPED')
));

// Mirrors BE NEEDS_ACTION reasons (api-contract.md, Admin routes).
export const NEEDS_ACTION_REASON_LABELS = {
  ISSUE_FAILED: 'Xuất vé thất bại',
  WAITING_FOR_REFUND: 'Chờ hoàn tiền',
  DUPLICATE_PAYMENT: 'Thanh toán trùng',
  AMOUNT_MISMATCH: 'Lệch tiền thanh toán',
  SEAT_CHANGE_FAILED: 'Đổi ghế lỗi',
  EXCHANGE_FAILED: 'Đổi vé lỗi',
  EXCHANGE_REFUND_DUE: 'Chờ hoàn tiền đổi vé',
  SCHEDULE_CHANGE: 'Hãng đổi lịch bay',
  EXTRAS_FAILED: 'Mua thêm dịch vụ lỗi',
  // Derived by the BE: an issueticket answer that never arrived, waited out (BE 1c0c90d).
  ISSUE_UNCONFIRMED: 'Chưa rõ kết quả xuất vé',
};

export const NEEDS_ACTION_REASON_OPTIONS = Object.entries(NEEDS_ACTION_REASON_LABELS)
  .map(([value, label]) => ({ value, label }));

// The status badge already says these two.
const STATUS_REASONS = ['ISSUE_FAILED', 'WAITING_FOR_REFUND'];

export const getReasonFlags = (reasons = []) => reasons
  .filter((reason) => !STATUS_REASONS.includes(reason))
  .map((reason) => NEEDS_ACTION_REASON_LABELS[reason] || reason);

// Mirrors BE seatChanges[].status (post-booking-changes.md §3.3).
export const SEAT_CHANGE_STATUS_LABELS = {
  PENDING_PAYMENT: 'Chờ thanh toán',
  APPLYING: 'Đang áp dụng',
  APPLIED: 'Đã áp dụng',
  FAILED: 'Thất bại',
  EXPIRED: 'Hết hạn thanh toán',
};

// Mirrors BE exchanges[].status (post-booking-changes.md §3.5).
export const EXCHANGE_STATUS_LABELS = {
  QUOTED: 'Đã báo giá',
  PENDING_PAYMENT: 'Chờ thanh toán',
  APPLYING: 'Đang đổi vé',
  APPLIED: 'Đã đổi vé',
  FAILED: 'Thất bại',
  EXPIRED: 'Hết hạn báo giá',
};

// What a manual refund of an exchange covers (PM rule, post-booking-changes.md §3.5):
// PAYMENT = the customer paid for an exchange that failed, AIRLINE = the airline's refundDue.
export const getExchangeRefundOwed = (entry) => {
  if (entry.refundedAt) return null;
  if (entry.status === 'FAILED' && entry.paidAt) return { kind: 'PAYMENT', amount: entry.amountDue };
  if (entry.status === 'APPLIED' && entry.refundDue > 0) {
    return { kind: 'AIRLINE', amount: entry.refundDue };
  }
  return null;
};

export const formatSeatMove = ({ from, to }) => `${from || 'Chưa có ghế'} → ${to || 'Bỏ ghế'}`;

// Mirrors BE scheduleChanges[].source (schedule-changes.md §2.1).
export const SCHEDULE_CHANGE_SOURCE_LABELS = {
  SYNC: 'Đồng bộ',
  ISSUE: 'Sau xuất vé',
  SCAN: 'Quét hằng ngày',
};

// Display label for a resolved schedule change (schedule-changes.md §2.3); the modal's own
// choice labels ask the admin what to do, this names what was done.
export const SCHEDULE_CHANGE_RESOLUTION_LABELS = {
  RESEATED: 'Xếp lại chỗ',
  REFUNDED: 'Đã hoàn phí ghế',
  NO_ACTION: 'Không cần xử lý',
};

// Mirrors BE refund.source (WP-BE-HNH-1 item 3): only VN answers a refund quote
// (PARTNER_REFUND_SYSTEMS); every other refund is agreed with the airline by hand.
export const REFUND_SOURCE_LABELS = {
  MANUAL: 'Thủ công',
  PARTNER: 'Từ hãng',
};

// refund.refundedBy is the admin's id on a manual refund, or the string 'AUTO' when the order
// refunded itself (an instant book the airline refused, or a payment after the window). On AUTO,
// refund.source is a DTO default ('PARTNER') and must not be shown (phase-2-remaining.md §3).
export const formatRefundSource = (refund) => (
  refund?.refundedBy === 'AUTO'
    ? 'Hoàn tự động'
    : (REFUND_SOURCE_LABELS[refund?.source] || refund?.source)
);

// Mirrors BE payment.refunds[].status (onepay-refund.md §3.2). REQUESTED means OnePay never
// answered — nothing says whether the money moved.
export const PAYMENT_REFUND_STATUS_LABELS = {
  REQUESTED: 'Đã gửi lệnh',
  APPROVED: 'Đã duyệt',
  PENDING: 'Đang xử lý',
  FAILED: 'Bị từ chối',
};

// AppBadge colors for the same statuses, by how safe each one reads for the admin.
export const PAYMENT_REFUND_STATUS_COLOR = {
  REQUESTED: 'neutral',
  APPROVED: 'success',
  PENDING: 'warning',
  FAILED: 'error',
};

// Mirrors BE payment.refunds[].action: which admin action sent this refund command.
export const PAYMENT_REFUND_ACTION_LABELS = {
  MARK_REFUNDED: 'Đánh dấu đã hoàn tiền',
  RESOLVE_DUPLICATE: 'Xử lý thanh toán trùng',
  RESOLVE_AMOUNT_MISMATCH: 'Xử lý lệch tiền thanh toán',
  RESOLVE_SEAT_CHANGE: 'Xử lý đổi ghế lỗi',
  RESOLVE_EXTRAS: 'Xử lý mua thêm dịch vụ lỗi',
  MARK_EXCHANGE_REFUNDED: 'Đánh dấu đã hoàn tiền đổi vé',
  // Sent by the order itself, not an admin (phase-2-remaining.md §3): operator 'AUTO'.
  INSTANT_FAILED: 'Hãng từ chối xuất vé gấp',
  INSTANT_LATE_PAYMENT: 'Thanh toán sau hạn vé gấp',
};

// One RefundLedger row from a payment.refunds[] entry (the shared component takes no product
// vocabulary of its own, so every cell is formatted here).
export const formatPaymentRefund = (refund, currency) => ({
  id: refund.id,
  time: formatFlightDateTime(refund.requestedAt),
  action: PAYMENT_REFUND_ACTION_LABELS[refund.action] || refund.action,
  amount: formatFlightAmount(refund.amount, currency),
  status: {
    label: PAYMENT_REFUND_STATUS_LABELS[refund.status] || refund.status,
    color: PAYMENT_REFUND_STATUS_COLOR[refund.status] || 'neutral',
  },
  result: refund.transactionNo
    ? `Mã giao dịch OnePay ${refund.transactionNo}`
    : (refund.message || '-'),
});

// Flight schedules read time-first (schedule-changes.md §2.5 copy: "10:25 30/09/2026").
export const formatFlightTimeDate = (date) => (
  convertIsoDateToString(date, 'HH:mm DD/MM/YYYY') || '-'
);

const formatScheduleChangeFlight = (flight) => {
  const segment = (flight?.segments || [])[0] || {};
  const carrier = segment.marketingCarrier || flight?.airline || '';
  return `${carrier}${segment.flightNumber || ''} · ${formatFlightTimeDate(flight?.startAt)}`;
};

// Old → new per leg; a cancelled leg (no replacement flight) says so and never promises a
// refund — the admin decides that in the resolve modal (schedule-changes.md §2.5).
export const formatScheduleChangeLeg = ({ leg, oldFlight, newFlight }, tripType) => (
  `${formatLeg(leg, tripType)}: ${formatScheduleChangeFlight(oldFlight)} → ${
    newFlight ? formatScheduleChangeFlight(newFlight) : 'Hãng đã huỷ chặng này'}`
);

// SCHEDULE_CHANGE needs-action rule mirrored client-side (api-contract.md, GET /flight-bookings):
// a seat lost with a price, or a leg the airline no longer shows, and not yet resolved.
export const isScheduleChangeResolvable = (entry) => !entry.resolution && (
  (entry.seatsLost || []).some((seat) => seat.price > 0)
  || (entry.legs || []).some((leg) => !leg.newFlight)
);

// The e-ticket comes through axios (Bearer), so a plain link cannot open it. The caller opens
// the tab on the click (popup blockers allow that) and this fills it once the HTML arrives.
export const showHtmlInTab = (tab, html) => {
  tab.location.replace(URL.createObjectURL(new Blob([html], { type: 'text/html' })));
};

// Axios gives no response when the request never got an answer (offline, a timeout, or a proxy
// error page without CORS headers); the shared handler would toast the bare "Network Error".
export const handleFlightApiError = (error) => {
  if (error?.isAxiosError && !error.response) {
    showToastError('Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.');
    return;
  }
  apiErrorHandler(error);
};

// Failed page load: a 403 means the role lacks the module; anything else may pass on a retry.
export const getLoadErrorText = (error, subject, retryHint) => (
  error?.response?.status === 403
    ? `Bạn không có quyền xem ${subject}`
    : `Không tải được ${subject}. ${retryHint}`
);

export const toSystemOptions = (systems) => systems.map(({ code, name }) => ({
  value: code,
  label: `${name} (${code})`,
}));

export const FLIGHT_BOOKING_TAB = {
  ALL: 'ALL',
  NEEDS_ACTION: 'NEEDS_ACTION',
};

export const FLIGHT_SEARCH_PLACEHOLDER = 'Mã đơn (FL...), PNR, số vé, email hoặc SĐT — nhập đúng hoặc phần đầu';

export const SERVICE_FEE_TYPE = {
  FIXED: 'FIXED',
  PERCENT: 'PERCENT',
};

export const SERVICE_FEE_TYPE_OPTIONS = [
  { value: SERVICE_FEE_TYPE.FIXED, label: 'Số tiền cố định (VND)' },
  { value: SERVICE_FEE_TYPE.PERCENT, label: 'Phần trăm (%)' },
];

// BE intranet modules (bookese-nodejs src/constants/intranetPermissions.js).
// Flight revenue shares the REVENUE module with the hotel revenue routes.
export const FLIGHT_MODULE = {
  BOOKING: 'FLIGHT_BOOKING',
  REFUND: 'FLIGHT_REFUND',
  SETTING: 'FLIGHT_SETTING',
  REVENUE: 'REVENUE',
};

// Intranet pages gated by the modules[] from GET /profile-intranet/my-permission.
// Any one listed module opens the page.
export const FLIGHT_ROUTE_MODULES = {
  [ENDPOINT.MANAGER_FLIGHT_BOOKING]: [FLIGHT_MODULE.BOOKING, FLIGHT_MODULE.REFUND],
  [ENDPOINT.MANAGER_FLIGHT_SETTING]: [FLIGHT_MODULE.SETTING],
  [ENDPOINT.MANAGER_FLIGHT_REVENUE]: [FLIGHT_MODULE.REVENUE],
};
